import { getBaseThemeName } from "@/features/branding";
import { UTF8_BOM } from "@/features/custom_downloads/utils";
import { unparse } from "@climaterisk/papaparse";
import { ZipWriterStream } from "@zip.js/zip.js";
import { Workbook } from "exceljs";
import { getGlossaryRows } from "./glossary";
import { getReadmeRows, getReadmeText, README_HEADING, readmeHeadings } from "./readme";
import { AUTO_RESILIENCE_SETTINGS_TITLE, getSettingsRows } from "./settings";

const GLOSSARY_SHEET_NAME = "Glossary";
export const GLOSSARY_FILENAME = `${GLOSSARY_SHEET_NAME}.csv`;
export const AUTO_RESILIENCE_SETTINGS_FILENAME = `${AUTO_RESILIENCE_SETTINGS_TITLE}.csv`;

export interface CommonOutputOptions {
  /** Drops the resilience glossary terms and the settings table when false. */
  showResilience: boolean;
  /** Selects the region's default input values in the shared transformers. */
  regionType?: string;
  readmeTitle?: string;
}

const buildTextStream = (text: string) =>
  new ReadableStream({
    start(controller) {
      controller.enqueue(new TextEncoder().encode(text));
      controller.close();
    },
  });

const buildCsvStream = (rows: string[][]) =>
  new ReadableStream({
    start(controller) {
      controller.enqueue(UTF8_BOM);
      controller.enqueue(new TextEncoder().encode(unparse(rows) + "\n"));
      controller.close();
    },
  });

export const addReadmeToZip = (zipWriterStream: ZipWriterStream, options?: CommonOutputOptions) => {
  void buildTextStream(getReadmeText(options?.readmeTitle)).pipeTo(zipWriterStream.writable(`${README_HEADING}.txt`));
};

export const addGlossaryToZip = (zipWriterStream: ZipWriterStream, options: CommonOutputOptions) => {
  void buildCsvStream(getGlossaryRows(options)).pipeTo(zipWriterStream.writable(GLOSSARY_FILENAME));
};

export const addResilienceSettingsToZip = (zipWriterStream: ZipWriterStream, options: CommonOutputOptions) => {
  // TEMP: HKMA does not ship the settings table, though it keeps the pathway column and glossary.
  if (!options.showResilience || getBaseThemeName() === "hkma") return;
  void buildCsvStream(getSettingsRows()).pipeTo(zipWriterStream.writable(AUTO_RESILIENCE_SETTINGS_FILENAME));
};

export const addCommonFilesToZip = (zipWriterStream: ZipWriterStream, options: CommonOutputOptions) => {
  addReadmeToZip(zipWriterStream, options);
  addGlossaryToZip(zipWriterStream, options);
};

/** Adds the README and Glossary sheets. Call before any data sheets, so these lead. */
export const addCommonSheetsToWorkbook = (book: Workbook, options: CommonOutputOptions) => {
  const readme = book.addWorksheet(README_HEADING);
  readme.addRows(getReadmeRows(options.readmeTitle));
  readme.getColumn(1).width = 120;
  readme.getColumn(1).alignment = { wrapText: true, vertical: "top" };
  readme.eachRow((row, index) => {
    const isTitle = index === 1;
    row.font = {
      size: isTitle ? 18 : 11,
      bold: isTitle || readmeHeadings.includes(String(row.getCell(1).value)),
    };
  });

  const glossary = book.addWorksheet(GLOSSARY_SHEET_NAME);
  glossary.addRows(getGlossaryRows(options));
  glossary.getRow(1).font = { bold: true };
  glossary.getColumn(1).width = 40;
  glossary.getColumn(2).width = 100;
  glossary.getColumn(2).alignment = { wrapText: true, vertical: "top" };
};
